
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Wand2, Loader2, Link2, Check } from "lucide-react";
import { supabase } from '@/integrations/supabase/client';
import { useToast } from "@/hooks/use-toast";
import { useTheme } from "next-themes";
import { getThemeColor } from "@/utils/colorUtils";

function extractMindNoteShareId(rawUrl: string): string | null {
  try {
    const parsed = new URL(rawUrl);
    const host = parsed.hostname.toLowerCase();
    const isMindNoteHost = host.endsWith('mindnote.online') || host.endsWith('lovable.app');
    if (!isMindNoteHost) return null;
    const match = parsed.pathname.match(/\/share\/([A-Za-z0-9_-]+)/i);
    return match?.[1] ?? null;
  } catch {
    const match = rawUrl.match(/\/share\/([A-Za-z0-9_-]+)/i);
    return match?.[1] ?? null;
  }
}
const URL_RE = /https?:\/\/[^\s)]+/gi;

function extractUrls(text: string): string[] {
  return Array.from(new Set((text.match(URL_RE) ?? []).map((u) => u.replace(/[.,;:!?)\]]+$/, ''))));
}

async function fetchJina(url: string, timeoutMs = 5000): Promise<string | null> {
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    try {
      const res = await fetch(`https://r.jina.ai/${url}`, {
        headers: { Accept: 'text/plain' },
        signal: ctrl.signal,
      });
      if (!res.ok) return null;
      return (await res.text()).slice(0, 5000);
    } finally {
      clearTimeout(timer);
    }
  } catch {
    return null;
  }
}

interface AIChatInterfaceProps {
  content: string;
  onContentUpdate: (newContent: string) => void;
  selectedText?: { start: number; end: number; text: string; } | null;
  noteColor?: string;
  noteId?: string | null;
}

const AIChatInterface: React.FC<AIChatInterfaceProps> = ({ content, onContentUpdate, selectedText, noteColor = '#F2FCE2', noteId }) => {
  const [prompt, setPrompt] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [estimatedTime, setEstimatedTime] = useState(0);
  const [remainingTime, setRemainingTime] = useState(0);
  const { toast } = useToast();
  const { theme } = useTheme();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Get theme-appropriate note color
  const themeNoteColor = getThemeColor(noteColor, theme === 'dark');
  
  // Create lighter/darker variants for background and borders
  const getColorVariants = (color: string) => {
    // Extract RGB values from hex color
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Calculate luminance to determine if we need light or dark text
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    const isDark = luminance < 0.5;
    
    return {
      background: theme === 'dark' ? `rgba(${r}, ${g}, ${b}, 0.15)` : `rgba(${r}, ${g}, ${b}, 0.1)`,
      border: theme === 'dark' ? `rgba(${r}, ${g}, ${b}, 0.3)` : `rgba(${r}, ${g}, ${b}, 0.2)`,
      accent: color,
      text: theme === 'dark' 
        ? (isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.8)')
        : (isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.8)'),
      secondaryText: theme === 'dark'
        ? (isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)')
        : (isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.6)')
    };
  };
  
  const colors = getColorVariants(themeNoteColor);

  // Estimate processing time based on text length with conservative estimates for longer texts
  const estimateProcessingTime = (text: string): number => {
    const charCount = text.length;
    const wordCount = text.split(/\s+/).length;
    
    // Base overhead (API latency, connection time, model loading)
    const baseTime = 5;
    
    // Calculate processing time more conservatively to avoid long "finalizing..." periods
    // Use both character and word count for better accuracy
    let processingTime: number;
    
    if (charCount > 100000) {
      // Very long text: ~0.001s per char + word-based scaling + complexity buffer
      processingTime = baseTime + (charCount * 0.0012) + (wordCount * 0.020) + 20;
    } else if (charCount > 50000) {
      // Long text: ~0.0008s per char + word-based scaling + buffer
      processingTime = baseTime + (charCount * 0.0010) + (wordCount * 0.018) + 15;
    } else if (charCount > 20000) {
      // Medium-long text: ~0.0006s per char + word-based scaling + buffer
      processingTime = baseTime + (charCount * 0.0008) + (wordCount * 0.015) + 10;
    } else if (charCount > 10000) {
      // Medium text
      processingTime = baseTime + (charCount * 0.0006) + (wordCount * 0.012) + 8;
    } else if (charCount > 5000) {
      // Short-medium text
      processingTime = baseTime + (charCount * 0.0004) + (wordCount * 0.010) + 5;
    } else if (charCount > 1000) {
      // Short text
      processingTime = baseTime + (charCount * 0.0020) + (wordCount * 0.008);
    } else if (charCount > 100) {
      // Very short text
      processingTime = baseTime + (charCount * 0.015) + (wordCount * 0.005);
    } else {
      // Minimal text
      processingTime = baseTime + 3;
    }
    
    // Round up and ensure minimum of 8 seconds
    return Math.max(Math.ceil(processingTime), 8);
  };

  // Countdown timer effect
  useEffect(() => {
    if (isProcessing) {
      const textToProcess = selectedText ? selectedText.text : content;
      const estimated = estimateProcessingTime(textToProcess);
      setEstimatedTime(estimated);
      setRemainingTime(estimated);
      
      timerRef.current = setInterval(() => {
        setRemainingTime(prev => Math.max(prev - 1, 0));
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setRemainingTime(0);
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isProcessing, selectedText, content]);

  const cleanupUnwantedSymbols = (text: string, originalContent: string): string => {
    // Only remove * or # if they weren't in the original content
    let cleanedText = text;
    
    if (!originalContent.includes('*')) {
      cleanedText = cleanedText.replace(/\*/g, '');
    }
    
    if (!originalContent.includes('#')) {
      cleanedText = cleanedText.replace(/#/g, '');
    }
    
    return cleanedText.trim();
  };

  const stripEmbeddedImageDataForAi = (text: string): string => text
    // Large pasted/OCR images are saved as base64 data URLs in markdown. Sending
    // those huge strings to the AI can make the request fail or time out before
    // any result reaches BlockNote, so keep only a readable image placeholder.
    .replace(/!\[([^\]]*)\]\(data:image\/[^)]+\)/gi, (_match, alt) => alt ? `[Image: ${alt}]` : '[Image]')
    .replace(/<img\b[^>]*src=["']data:image\/[^"']+["'][^>]*>/gi, '[Image]')
    .replace(/data:image\/[a-z0-9.+-]+;base64,[A-Za-z0-9+/=\r\n]+/gi, '[Image data removed]');

  const handlePromptChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPrompt(value);
    
    // Show hint when user starts typing
    if (value.trim() && !showHint) {
      setShowHint(true);
    } else if (!value.trim() && showHint) {
      setShowHint(false);
    }
  };

  const handleProcessText = async () => {
    if (!prompt.trim()) return;

    const liveContent = typeof (window as any).getBlockNoteMarkdown === 'function'
      ? await (window as any).getBlockNoteMarkdown().catch(() => undefined)
      : undefined;
    const currentContent = typeof liveContent === 'string' && (liveContent.trim() || !content.trim())
      ? liveContent
      : ((window as any).__mindnoteLatestBlockNoteMarkdown ?? content ?? '');

    // Check if this is an "additive" command (keep existing text and add more)
    // Support multiple languages for "keep this text" and "add" commands
    const isAdditiveCommand = /\b(keep\s+this\s+text\s+and|add\b|mantén\s+este\s+texto\s+y|agregar|añadir|garder\s+ce\s+texte\s+et|ajouter|behalt\s+diesen\s+text\s+und|hinzufügen|mantieni\s+questo\s+testo\s+e|aggiungi|このテキストを保持して|追加|保持这个文本|添加|守住這個文字|新增|сохрани\s+этот\s+текст\s+и|добавь|manter\s+este\s+texto\s+e|adicionar|behoud\s+deze\s+tekst\s+en|toevoegen|zachowaj\s+ten\s+tekst\s+i|dodaj|bevar\s+denne\s+tekst\s+og|læg\s+til|bevara\s+denna\s+text\s+och|lägg\s+till)/i.test(prompt);
    
    // Use selected text if available, otherwise use full content
    const textToProcess = selectedText ? selectedText.text : currentContent;
    const textForAi = stripEmbeddedImageDataForAi(textToProcess);

    setIsProcessing(true);
    try {
      let instructionToSend = prompt;
      let contextUrlsForServer: string[] = [];
      let contextBlocksForServer: string[] = [];

      // For additive commands, modify the instruction to be clearer
      if (isAdditiveCommand && !selectedText && currentContent.trim()) {
        instructionToSend = `Based on this existing text, ${prompt}. Only return the NEW content to be added, not the existing text.`;
      }

      // Resolve any links the user flagged as "Use as AI context" for this note.
      // For MindNote share links, also pull AI-context URLs flagged in the source note.
      try {
        const keys = [`note_ai_context_urls_${noteId ?? 'new'}`];
        // Also read the 'new' bucket as a safety net for links added before
        // the note was first saved (in case migration on save didn't run).
        if (noteId) keys.push('note_ai_context_urls_new');
        const flagged: string[] = [];
        for (const k of keys) {
          try {
            const raw = JSON.parse(localStorage.getItem(k) ?? '[]');
            if (Array.isArray(raw)) raw.forEach((u) => typeof u === 'string' && flagged.push(u));
          } catch { /* ignore */ }
        }
        // Honor every URL the user explicitly flagged as AI context. We used
        // to require `content.includes(url)`, but BlockNote escapes URL
        // characters when serializing markdown (parens, spaces, etc.), so
        // the raw URL often isn't found verbatim — that silently dropped
        // every flagged link and the model received no context.
        const directUrls = Array.from(new Set(
          flagged.filter((url): url is string => typeof url === 'string' && /^https?:\/\//i.test(url))
        ));


        // Expand MindNote share URLs by fetching each shared note and adding ITS context URLs.
        const expanded = new Set<string>(directUrls);
        const sharedNotePreviews: string[] = [];
        for (const url of directUrls.slice(0, 5)) {
          const shareId = extractMindNoteShareId(url);
          if (!shareId) continue;
          try {
            const { data: sharedNote, error: sharedNoteError } = await supabase
              .rpc('get_shared_note', { p_share_id: shareId });
            if (sharedNoteError || !sharedNote) {
              sharedNotePreviews.push(`--- Linked MindNote: ${url} ---\n(Could not load the linked note contents, but the user explicitly marked this note link as relevant context.)`);
              continue;
            }
            const sharedContent: string = sharedNote.content ?? '';
            const sharedTitle: string = sharedNote.title ?? 'Linked note';
            // Use the linked note's own text as context too (truncated).
            const cleaned = sharedContent
              .replace(/<ai-generated>([\s\S]*?)<\/ai-generated>/g, '$1')
              .replace(/<[^>]+>/g, ' ')
              .slice(0, 4000);
            if (cleaned.trim()) {
              sharedNotePreviews.push(`--- Linked MindNote: ${sharedTitle} (${url}) ---\n${cleaned}`);
            }
            // Pull the linked note's flagged AI-context URLs from local storage if same user.
            try {
              const linkedKey = `note_ai_context_urls_${sharedNote.id}`;
              const linkedFlagged: string[] = JSON.parse(localStorage.getItem(linkedKey) ?? '[]');
              if (Array.isArray(linkedFlagged)) {
                linkedFlagged.forEach((u) => typeof u === 'string' && expanded.add(u));
              }
            } catch { /* ignore */ }
            // Also auto-include any URLs found in the linked note's content.
            extractUrls(sharedContent).forEach((u) => expanded.add(u));
          } catch (e) {
            sharedNotePreviews.push(`--- Linked MindNote: ${url} ---\n(Could not load the linked note contents, but the user explicitly marked this note link as relevant context.)`);
            console.warn('Failed to expand shared-note context', url, e);
          }
        }

        // External URLs are fetched SERVER-SIDE in the process-text edge
        // function (avoids CORS, browser rate limits, and abort timeouts).
        const allContextUrls = Array.from(expanded);
        const fetchableUrls = allContextUrls.filter((u) => !extractMindNoteShareId(u));

        contextUrlsForServer = fetchableUrls.slice(0, 6);
        contextBlocksForServer = sharedNotePreviews;
      } catch (e) {
        console.warn('Link-context resolution failed', e);
      }

      const { data, error } = await supabase.functions.invoke('process-text', {
        body: {
          text: textForAi,
          instruction: instructionToSend,
          contextUrls: contextUrlsForServer,
          contextBlocks: contextBlocksForServer,
        }
      });

      if (error) {
        // supabase-js wraps non-2xx as FunctionsHttpError with a generic
        // message. Try to read the actual server error body so we (and the
        // user) can see WHY the call failed instead of a meaningless
        // "non-2xx status code".
        let serverMessage: string | undefined;
        try {
          const ctxRes = (error as any)?.context;
          if (ctxRes && typeof ctxRes.json === 'function') {
            const parsed = await ctxRes.clone().json().catch(() => null);
            serverMessage = parsed?.error ?? parsed?.message;
          } else if (ctxRes && typeof ctxRes.text === 'function') {
            serverMessage = await ctxRes.clone().text().catch(() => undefined);
          }
        } catch { /* ignore */ }
        throw new Error(serverMessage || (error as any)?.message || 'AI request failed');
      }
      if (!data?.result?.trim()) {
        throw new Error(data?.error ?? 'AI returned an empty response');
      }

      
      const cleanedResult = cleanupUnwantedSymbols(data.result, textForAi);
      
      // Wrap AI-generated content with a marker for visual distinction
      const aiMarkedContent = `<ai-generated>${cleanedResult}</ai-generated>`;
      const replaceBlockNoteMarkdown = async (nextContent: string) => {
        const replaceInBlockNote = (window as any).replaceBlockNoteMarkdown as
          | ((markdown: string) => Promise<boolean>)
          | undefined;
        if (typeof replaceInBlockNote === 'function') {
          const replaced = await replaceInBlockNote(nextContent).catch(() => false);
          if (replaced) return true;
        }
        onContentUpdate(nextContent);
        return false;
      };
      
      if (selectedText) {
        // Replace only the selected text. The start/end offsets come from
        // BlockNote's visible-text positions, which don't line up with the
        // markdown string. Try the offsets first; if they don't match the
        // selected text, fall back to a substring search so we still only
        // touch the selected region instead of overwriting the whole note.
        const sel = selectedText.text;
        let newContent: string | null = null;
        if (
          typeof selectedText.start === 'number' &&
          typeof selectedText.end === 'number' &&
          currentContent.substring(selectedText.start, selectedText.end) === sel
        ) {
          newContent =
            currentContent.substring(0, selectedText.start) +
            aiMarkedContent +
            currentContent.substring(selectedText.end);
        } else {
          const idx = currentContent.indexOf(sel);
          if (idx !== -1) {
            newContent =
              currentContent.substring(0, idx) +
              aiMarkedContent +
              currentContent.substring(idx + sel.length);
          }
        }
        if (newContent === null) {
          // Last-resort fallback: insert at caret so we never overwrite the
          // whole note when we can't locate the selection in markdown.
          const bnInsert = (window as any).insertAtBlockNoteCaret as
            | ((md: string) => Promise<boolean>)
            | undefined;
          let inserted = false;
          if (typeof bnInsert === 'function') {
            try { inserted = await bnInsert(aiMarkedContent); } catch { inserted = false; }
          }
          if (!inserted) await replaceBlockNoteMarkdown(`${currentContent.trimEnd()}\n\n${aiMarkedContent}`.trimStart());
        } else {
          await replaceBlockNoteMarkdown(newContent);
        }
      } else {
        // Insert at the current caret position. Prefer the BlockNote-aware
        // helper so the new content lands exactly where the user was typing.
        const bnInsert = (window as any).insertAtBlockNoteCaret as
          | ((md: string) => Promise<boolean>)
          | undefined;
        const beforeMd =
          (window as any).__mindnoteLatestBlockNoteMarkdown ?? currentContent ?? '';
        let inserted = false;
        if (!currentContent.trim()) {
          await replaceBlockNoteMarkdown(aiMarkedContent);
          inserted = true;
        } else if (typeof bnInsert === 'function') {
          try {
            inserted = await bnInsert(aiMarkedContent);
          } catch {
            inserted = false;
          }
        }

        // Fallback chain ensures the AI result always renders.
        if (!inserted) {
          const bnAppend = (window as any).appendToBlockNoteEnd;
          if (typeof bnAppend === 'function') {
            try {
              const appended = await bnAppend(aiMarkedContent);
              inserted = !!appended;
            } catch {
              inserted = false;
            }
          }
        }

        if (!inserted) {
          const sep = beforeMd && beforeMd.trim() ? beforeMd.trimEnd() + '\n\n' : '';
          await replaceBlockNoteMarkdown(sep + aiMarkedContent);
        }
      }


      // Fire-and-forget: refresh writing-style profile if it's stale.
      supabase.functions.invoke('update-style-profile', { body: {} }).catch(() => {});
      
      toast({
        title: "Text processed",
        description: isAdditiveCommand ? "New content has been added successfully." : 
                    selectedText ? "Selected text has been updated successfully." : 
                    "Your text has been updated successfully.",
      });
    } catch (error) {
      console.error('Error processing text:', error);
      const message = error instanceof Error ? error.message : "Failed to process text. Please try again.";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
      } finally {
        setIsProcessing(false);
        setPrompt('');
        // Keep hint visible so instructions remain available
      }
  };

  // Detect a URL inside the current selection (for "Add as AI context" shortcut).
  const selectedUrl = useMemo(() => {
    const t = selectedText?.text?.trim() ?? '';
    if (!t) return null;
    const m = t.match(URL_RE);
    return m && m.length === 1 ? m[0].replace(/[.,;:!?)\]]+$/, '') : null;
  }, [selectedText]);

  // Track which URLs are already flagged as AI context for this note.
  const contextKey = `note_ai_context_urls_${noteId ?? 'new'}`;
  const [contextUrls, setContextUrls] = useState<string[]>(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(contextKey) ?? '[]');
      return Array.isArray(raw) ? raw : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(contextKey) ?? '[]');
      setContextUrls(Array.isArray(raw) ? raw : []);
    } catch {
      setContextUrls([]);
    }
  }, [contextKey, selectedUrl]);

  const isSelectedUrlInContext = !!selectedUrl && contextUrls.includes(selectedUrl);

  const handleAddSelectedAsContext = () => {
    if (!selectedUrl) return;
    try {
      const existing = JSON.parse(localStorage.getItem(contextKey) ?? '[]');
      const list = Array.isArray(existing) ? existing : [];
      if (!list.includes(selectedUrl)) {
        list.push(selectedUrl);
        localStorage.setItem(contextKey, JSON.stringify(list));
      }
      setContextUrls(list);
      toast({
        title: "Added to AI context",
        description: "The AI will read this link when answering in this note.",
      });
    } catch (e) {
      console.warn('Failed to add link to AI context', e);
    }
  };

  const handleRemoveSelectedFromContext = () => {
    if (!selectedUrl) return;
    try {
      const existing = JSON.parse(localStorage.getItem(contextKey) ?? '[]');
      const list = (Array.isArray(existing) ? existing : []).filter((u: string) => u !== selectedUrl);
      localStorage.setItem(contextKey, JSON.stringify(list));
      setContextUrls(list);
      toast({
        title: "Removed from AI context",
        description: "The AI will no longer read this link in this note.",
      });
    } catch (e) {
      console.warn('Failed to remove link from AI context', e);
    }
  };

  return (
    <div className="space-y-2">
      {selectedText && (
        <div 
          className="text-sm rounded-md p-3 mb-2"
          style={{ 
            backgroundColor: colors.background,
            border: `1px solid ${colors.border}`
          }}
        >
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2 min-w-0">
              <div 
                className="w-2 h-2 rounded-full shrink-0"
                style={{ backgroundColor: colors.accent }}
              ></div>
              <span 
                className="font-medium truncate"
                style={{ color: colors.text }}
              >
                Selected text will be modified
              </span>
            </div>
            {selectedUrl && (
              isSelectedUrlInContext ? (
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleRemoveSelectedFromContext}
                  title="Click to remove from AI context"
                  className="inline-flex items-center gap-1 text-xs shrink-0 hover:underline"
                  style={{ color: colors.accent }}
                >
                  <Check className="h-3 w-3" />
                  Added as AI context
                </button>
              ) : (
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleAddSelectedAsContext}
                  className="inline-flex items-center gap-1 text-xs hover:underline shrink-0"
                  style={{ color: colors.secondaryText }}
                >
                  <Link2 className="h-3 w-3" />
                  Use link as AI context
                </button>
              )
            )}
          </div>
          <div 
            className="text-xs p-2 rounded border-l-2"
            style={{ 
              color: colors.secondaryText,
              backgroundColor: colors.background,
              borderLeftColor: colors.accent
            }}
          >
            "{selectedText.text.length > 100 ? selectedText.text.substring(0, 100) + '...' : selectedText.text}"
          </div>
        </div>
      )}

      <div className="flex gap-2 items-center">
        <Input
          placeholder={selectedText ? "Enter instruction for selected text..." : "Enter instruction (e.g., 'translate to Spanish' or 'correct grammar')"}
          value={prompt}
          onChange={handlePromptChange}
          className="flex-1"
          data-tour="ai-input"
        />
        <Button 
          onClick={handleProcessText}
          disabled={isProcessing || !prompt.trim()}
          variant="outline"
          data-tour="ai-process"
          className="relative"
        >
          {isProcessing ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <Wand2 className="h-4 w-4 mr-2" />
              {selectedText ? "Process Selection" : "Process"}
            </>
          )}
        </Button>
      </div>
      
      {isProcessing && (
        <div className="text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded border-l-2 border-primary/30">
          <div className="flex items-center gap-1">
            <div className="flex gap-1">
              <div className="w-1 h-1 bg-primary/60 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
              <div className="w-1 h-1 bg-primary/60 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
              <div className="w-1 h-1 bg-primary/60 rounded-full animate-bounce"></div>
            </div>
            <span>AI is processing your request... {remainingTime > 0 ? `(estimated ${remainingTime}s remaining)` : '(finalizing...)'}</span>
          </div>
        </div>
      )}
      
      {showHint && (
        <div className="text-sm text-muted-foreground bg-muted p-3 rounded-md border">
          <strong>Tip:</strong> Use "Keep this text and..." or "Add..." to preserve your note and add more information. Other commands like "Summarize," "Translate," or "Create list" will modify the entire note.
          <br />
          <span className="text-xs opacity-75">Character limit: ~800,000. Very long texts may take 1-2 minutes to process.</span>
        </div>
      )}
    </div>
  );
};

export default AIChatInterface;
